import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// Example Job Data
const jobOpenings = [
  {
    id: 1,
    title: "Senior Full-Stack Developer (React & Node.js)",
    location: "Remote / Karachi",
    type: "Full-Time",
    description: "Design, develop, and deploy high-performance web applications. Requires 5+ years experience with the MERN stack and cloud services (AWS/Azure).",
    requirements: ["5+ years experience in React & Node.js", "Expertise in MongoDB and RESTful APIs", "Strong background in unit testing and CI/CD."],
    applyLink: "#apply-fullstack",
  },
  {
    id: 2,
    title: "UI/UX Designer (Figma Specialist)",
    location: "Karachi, On-Site",
    type: "Full-Time",
    description: "Create visually stunning and highly functional user interfaces. Focus on user-centric design principles and maintain our design system.",
    requirements: ["3+ years experience in UI/UX design", "Expert proficiency in Figma/Sketch", "Portfolio demonstrating strong visual design skills."],
    applyLink: "#apply-designer",
  },
  {
    id: 3,
    title: "Digital Marketing Specialist",
    location: "Remote",
    type: "Part-Time",
    description: "Lead our digital campaigns, focusing on SEO, social media, and content strategy to drive lead generation and brand visibility.",
    requirements: ["2+ years experience in Digital Marketing", "Proven track record with SEO/SEM tools", "Excellent content writing skills."],
    applyLink: "#apply-marketing",
  },
];

// Reusable Job Card Component
const JobCard = ({ job }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Gradient Border (Green to Cyan)
  const borderGradient = "linear-gradient(90deg, #00F5A0, #00CFFF)";

  return (
    <div
      className="p-5 sm:p-6 md:p-8 rounded-xl transition-all duration-300 relative overflow-hidden cursor-pointer"
      onClick={() => setIsOpen(!isOpen)}
      style={{
        // Glassmorphism Lite Properties (Same as ContactForm)
        backgroundColor: "rgba(255, 255, 255, 0.04)", // White 4%
        backdropFilter: "blur(10px)",
        // Custom Border using pseudo-element or border image (Using simple border for simplicity)
        border: `1px solid ${isOpen ? '#00F5A0' : 'rgba(255, 255, 255, 0.1)'}`, // Active state mein green border
        boxShadow: isOpen ? '0 0 20px rgba(0, 245, 160, 0.2)' : 'none', // Active state mein glow
      }}
    >
      <div className="flex justify-between items-start gap-4">
        {/* Title and Metadata */}
        <div>
          <h3 className="text-xl sm:text-2xl font-bold mb-1 text-white">
            {job.title}
          </h3>
          <p className="text-sm font-medium" style={{ color: "#00F5A0" }}>
            {job.location} • {job.type}
          </p>
        </div>

        {/* Expand/Collapse Icon */}
        <div 
          className="text-white text-2xl p-2 rounded-full transition-transform duration-300"
          style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          {isOpen ? '×' : '+'}
        </div>
      </div>

      {/* Expanded Content */}
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? 'max-h-96 pt-6 opacity-100' : 'max-h-0 pt-0 opacity-0'
        }`}
      >
        <p className="text-white/70 mb-4 leading-relaxed">{job.description}</p>
        
        <h4 className="font-semibold text-white mb-2" style={{ color: "#00CFFF" }}>
            Key Requirements:
        </h4>
        <ul className="list-disc list-inside text-white/70 space-y-1 ml-4">
          {job.requirements.map((req, index) => (
            <li key={index} className="text-sm">{req}</li>
          ))}
        </ul>

        {/* Apply Button */}
        <a 
          href={job.applyLink}
          onClick={(e) => e.stopPropagation()} // Stop propagation to prevent card from closing
          className="
            mt-6 inline-block 
            px-6 py-2 rounded-full font-semibold 
            transition duration-300
            text-black hover:scale-105
            // Same CTA Gradient as Contact form for consistency
          "
          style={{
            background: "linear-gradient(to right, #FF2D55, #FF005C)", // Pink Gradient
          }}
        >
          Apply Now →
        </a>
      </div>
    </div>
  );
};


export default function Career() {
  // Global styles (Agar aap inko separate CSS file mein move nahi kar rahe hain)
  const GlobalStyles = `
    /* Custom Scrollbar */
    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #000000; }
    ::-webkit-scrollbar-thumb { background: #00F5A0; border-radius: 4px; }
  `;

  return (
    <div
      className="min-h-screen text-white flex flex-col"
      style={{ backgroundColor: "#0B0E11" }}
    >
      <style>{GlobalStyles}</style>
      <Navbar />

      <div className="flex flex-col items-center flex-1 px-4 sm:px-6 md:px-10 lg:px-20 py-20 md:py-28">
        
        {/* Heading Section */}
        <div className="mb-10 md:mb-16 text-center">
            {/* Title: “JOIN” white, “OUR TEAM” gradient green → cyan */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-3">
                <span className="text-white">JOIN</span> 
                <span 
                    style={{
                        background: "linear-gradient(90deg, #00F5A0, #00CFFF)", // Green to Cyan Gradient
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}
                >
                    &nbsp;OUR TEAM
                </span>
            </h1>

            {/* Centered Short Gradient Line */}
            <div className="w-[60px] h-[3px] rounded-full mx-auto" 
                style={{ 
                    background: "linear-gradient(90deg, #00F5A0, #00CFFF)",
                    marginBottom: '15px' 
                }} 
            />

            {/* Subtitle */}
            <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                We are always looking for passionate people to join our mission of building next-generation digital products. Find your next opportunity below.
            </p>
        </div>


        {/* Job Listings Container */}
        <div className="w-full max-w-4xl space-y-6">
          {jobOpenings.map((job) => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
        
        {/* No Openings Message */}
        {jobOpenings.length === 0 && (
            <div className="text-center p-10 border border-white/10 rounded-xl max-w-xl mx-auto mt-10">
                <p className="text-lg text-white/50">
                    Currently, we don't have any open positions. Please check back later or send us your CV at <span className="text-[#00F5A0]">careers@crafttech.com</span>.
                </p>
            </div>
        )}

      </div>

      <Footer />
    </div>
  );
}